import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import { getAuthToken } from "../utils/auth";

interface ServiceStatus {
  status: "healthy" | "unhealthy" | "error";
  latencyMs?: number;
  error?: string | null;
}

interface HealthcheckResponse {
  timestamp: string;
  services: {
    sailthru: ServiceStatus;
  };
  overall: "healthy" | "degraded" | "error";
}

const SailthruHealthcheck: React.FC = () => {
  const [healthData, setHealthData] = useState<HealthcheckResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const fetchHealthcheck = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/prod/sailthru-healthcheck`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: HealthcheckResponse = await response.json();
      setHealthData(data);
      setLastChecked(new Date());
    } catch (err: any) {
      setError(err.message || "Failed to fetch healthcheck");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthcheck();
  }, [fetchHealthcheck]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircleIcon sx={{ color: "success.main" }} />;
      case "unhealthy":
      case "degraded":
        return <WarningIcon sx={{ color: "warning.main" }} />;
      case "error":
        return <ErrorIcon sx={{ color: "error.main" }} />;
      default:
        return null;
    }
  };

  const getStatusChip = (status: string) => {
    const colorMap: Record<string, "success" | "warning" | "error" | "default"> = {
      healthy: "success",
      unhealthy: "warning",
      degraded: "warning",
      error: "error",
    };

    return (
      <Chip
        icon={getStatusIcon(status) || undefined}
        label={status.toUpperCase()}
        color={colorMap[status] || "default"}
        size="small"
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4">Sailthru Healthcheck</Typography>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={fetchHealthcheck}
          disabled={loading}
        >
          {loading ? "Checking..." : "Refresh"}
        </Button>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "error.light" }}>
          <Typography color="error.contrastText">Error: {error}</Typography>
        </Paper>
      )}

      {healthData && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="h6">Overall Status:</Typography>
              {getStatusChip(healthData.overall)}
            </Box>
            {lastChecked && (
              <Typography variant="body2" color="text.secondary">
                Last checked: {lastChecked.toLocaleString()}
              </Typography>
            )}
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Latency</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight="medium">Sailthru</Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(healthData.services.sailthru.status)}</TableCell>
                  <TableCell>
                    {healthData.services.sailthru.latencyMs !== undefined
                      ? `${healthData.services.sailthru.latencyMs}ms`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {healthData.services.sailthru.error || "Connection successful"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {healthData.services.sailthru.error && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              <AlertTitle>Sailthru Connection Issue</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Error:</strong> {healthData.services.sailthru.error}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Common causes:
                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                  <li>IP address not on Sailthru allowlist</li>
                  <li>Invalid or expired API credentials</li>
                  <li>Sailthru service outage</li>
                </ul>
              </Typography>
            </Alert>
          )}
        </>
      )}

      {!healthData && !loading && !error && (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">No healthcheck data available.</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SailthruHealthcheck;
